/**
 * The application's only boundary to Google Places address suggestion.
 *
 * Callers hand over a host element and describe the field they want; this
 * module owns the field, the suggestion list, the provider calls, normalization
 * and teardown. No component imports a Google type, event name or result shape.
 *
 * The field is ours rather than Google's. Google's own widget exposes no event
 * for typing, so an address typed without choosing a suggestion would never
 * reach the form. See TCA-26092103.
 */
import { isDesktopOffline } from '@/api/desktopSync';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { loadGoogleMapsLibraries } from '@/utils/googleMapsLoader';

const SUGGEST_DEBOUNCE_MS = 200;
const MINIMUM_QUERY_LENGTH = 3;
const MAXIMUM_SUGGESTIONS = 5;

// Restricts rather than biases. `region` only biases and is deliberately unused.
const INCLUDED_REGION_CODES = ['us'];
// Primary types that represent a deliverable street address. Kept as one
// constant so a wrong filter is a one-line correction.
const INCLUDED_PRIMARY_TYPES = ['street_address', 'subpremise', 'premise'];

// The two selections the application actually consumes. Address entry fills
// four fields; community search stages a place by identity and coordinates.
export const ADDRESS_SELECTION = 'address';
export const PLACE_SELECTION = 'place';

const REQUESTED_FIELDS = {
  [ADDRESS_SELECTION]: ['addressComponents', 'formattedAddress'],
  [PLACE_SELECTION]: ['id', 'formattedAddress', 'location'],
};

export function googlePlacesAutocompleteAllowed() {
  return !isDesktopOffline();
}

function libraryIsUsable(library) {
  return Boolean(library?.AutocompleteSuggestion && library?.AutocompleteSessionToken);
}

export function loadGooglePlacesAutocomplete({ retry = false } = {}) {
  if (!googlePlacesAutocompleteAllowed()) return Promise.resolve(null);
  return loadGoogleMapsLibraries(['places'], { retry }).then(result => result.places);
}

/**
 * Street number and route become one street line; the rest of the fields fall
 * back through the locality shapes Google returns for Las Vegas addresses.
 */
function toAddressSelection(place) {
  const components = place?.addressComponents;
  if (!Array.isArray(components)) return null;

  const text = value => {
    if (value == null) return '';
    if (typeof value !== 'string') throw new TypeError('Invalid address text');
    return value;
  };
  const get = (type) => text(components.find((c) => c.types?.includes(type))?.longText);
  const getShort = (type) => text(components.find((c) => c.types?.includes(type))?.shortText);

  const streetNumber = get('street_number');
  // Match the save-time geocoder's short street name without guessing equivalence.
  const routeComponent = components.find((c) => c.types?.includes('route'));
  const usableRoute = value => typeof value === 'string' && value.trim() ? value : '';
  const route = usableRoute(routeComponent?.shortText) || usableRoute(routeComponent?.longText);
  if (!route) return null;

  return {
    street: streetNumber ? `${streetNumber} ${route}` : route,
    city: get('locality')
      || get('sublocality_level_1')
      || get('sublocality')
      || get('neighborhood')
      || get('administrative_area_level_3')
      || get('administrative_area_level_2')
      || '',
    state: getShort('administrative_area_level_1'),
    zip: get('postal_code'),
    formattedAddress: text(place?.formattedAddress),
  };
}

function coordinate(value) {
  // Google returns a LatLng, whose lat/lng are methods; a plain literal is
  // also accepted so a future shape change does not silently yield undefined.
  if (typeof value === 'function') return value();
  return value;
}

function toPlaceSelection(place) {
  if (!place?.id) return null;
  return {
    placeId: place.id,
    formattedAddress: place.formattedAddress || '',
    latitude: coordinate(place.location?.lat),
    longitude: coordinate(place.location?.lng),
  };
}

const NORMALIZERS = {
  [ADDRESS_SELECTION]: toAddressSelection,
  [PLACE_SELECTION]: toPlaceSelection,
};

function predictionLabel(prediction) {
  const main = prediction?.mainText?.toString?.() || '';
  const secondary = prediction?.secondaryText?.toString?.() || '';
  if (main) return { main, secondary };
  return { main: prediction?.text?.toString?.() || '', secondary: '' };
}

/**
 * Mount a Places-backed field inside `host`.
 *
 * Returns a handle for driving the field from React. Every path is safe: with
 * the desktop offline, the library unavailable, or a request failing, the field
 * is still a working text input and the handle still honours every setter.
 */
export function mountPlacesField(host, {
  kind = ADDRESS_SELECTION,
  value = '',
  placeholder = '',
  disabled = false,
  className = '',
  testId,
  onChange,
  onSelect,
  onReadyChange,
  onLoadStateChange,
} = {}) {
  if (!host) return null;

  let destroyed = false;
  let library = null;
  let sessionToken = null;
  let requestSequence = 0;
  let predictionSequence = 0;
  let predictionPending = false;
  let observedSessionRevision = getActiveSessionRevision();
  let suggestions = [];
  let activeIndex = -1;
  let debounceId = null;
  let loadSequence = 0;
  let loading = false;

  const input = document.createElement('input');
  input.type = 'text';
  input.value = value ?? '';
  input.placeholder = placeholder ?? '';
  input.disabled = Boolean(disabled);
  input.autocomplete = 'off';
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');
  if (className) input.className = className;
  if (testId) input.setAttribute('data-testid', testId);
  host.appendChild(input);

  // The panel lives inside the host, so a dialog treats a click on it as inside
  // itself. That is why no reparenting or outside-pointer exception is needed.
  const panel = document.createElement('div');
  panel.hidden = true;
  panel.className = 'absolute left-0 top-full z-[9999] mt-1 w-full overflow-hidden '
    + 'rounded-xl border border-stone-200 bg-white shadow-soft-xl';
  if (testId) panel.setAttribute('data-testid', `${testId}-suggestions`);

  const list = document.createElement('ul');
  list.setAttribute('role', 'listbox');
  list.className = 'py-1';
  panel.appendChild(list);

  // Required, not decorative: Google's policy obliges an attribution whenever
  // Places data is shown without a Google map. Their widget carried this for
  // us; rendering our own list makes it ours. Text rather than the logo, which
  // the policy permits where space is limited — and reproducing their mark
  // from memory would be its own brand problem.
  const attribution = document.createElement('div');
  attribution.className = 'border-t border-stone-100 px-3 py-1.5 text-right '
    + 'text-[10px] leading-none text-stone-400';
  attribution.textContent = 'Powered by Google Maps';
  panel.appendChild(attribution);

  host.appendChild(panel);

  // Recovery belongs to the mounted field: never recreate its input or replay
  // its text as a search just because Google becomes available (TCA-26101306).
  const loadStatus = document.createElement('div');
  loadStatus.className = 'mt-1 text-xs text-stone-500';
  loadStatus.hidden = true;
  const loadMessage = document.createElement('span');
  loadMessage.setAttribute('role', 'status');
  const retryButton = document.createElement('button');
  retryButton.type = 'button';
  retryButton.className = 'ml-2 underline disabled:opacity-50';
  retryButton.textContent = 'Try again';
  retryButton.hidden = true;
  if (testId) {
    loadStatus.setAttribute('data-testid', `${testId}-load-status`);
    retryButton.setAttribute('data-testid', `${testId}-retry`);
  }
  loadStatus.append(loadMessage, retryButton);
  host.appendChild(loadStatus);

  function publishLoadState(state, message = '', retryable = false) {
    loadStatus.hidden = state === 'ready';
    loadMessage.textContent = message;
    retryButton.hidden = !retryable;
    retryButton.disabled = state === 'loading';
    onLoadStateChange?.(state);
  }

  async function loadFieldLibrary(retry = false) {
    if (destroyed || loading || library) return;
    const sequence = ++loadSequence;
    const revision = getActiveSessionRevision();
    if (!googlePlacesAutocompleteAllowed()) {
      publishLoadState('unavailable', 'Address suggestions are unavailable offline.');
      return;
    }
    loading = true;
    publishLoadState('loading', 'Loading address suggestions…', retry);
    const isCurrent = () => !destroyed && sequence === loadSequence
      && revision === getActiveSessionRevision();
    try {
      const loaded = await loadGooglePlacesAutocomplete({ retry });
      if (!isCurrent()) return;
      if (!googlePlacesAutocompleteAllowed() || !libraryIsUsable(loaded)) {
        publishLoadState('unavailable', 'Address suggestions are unavailable. Your text is unchanged.');
        return;
      }
      library = loaded;
      freshToken();
      publishLoadState('ready');
      onReadyChange?.(true);
    } catch (error) {
      if (!isCurrent()) return;
      publishLoadState('unavailable', error?.message || 'Address suggestions are unavailable. Your text is unchanged.', Boolean(error?.retryable));
      onReadyChange?.(false);
    } finally {
      if (sequence === loadSequence) loading = false;
    }
  }

  const handleRetry = () => {
    if (!retryButton.matches(':disabled')) void loadFieldLibrary(true);
  };
  retryButton.addEventListener('click', handleRetry);

  function freshToken() {
    sessionToken = library ? new library.AutocompleteSessionToken() : null;
  }

  function closeList() {
    suggestions = [];
    activeIndex = -1;
    list.replaceChildren();
    panel.hidden = true;
    input.setAttribute('aria-expanded', 'false');
  }

  // Dismissal retires predictions, but not details deliberately chosen before
  // focus moved away. Setters, typing, session changes and teardown retire both.
  function dismissPredictions() {
    predictionSequence += 1;
    predictionPending = false;
    if (debounceId !== null) clearTimeout(debounceId);
    debounceId = null;
    closeList();
  }

  function invalidate({ resetToken = true } = {}) {
    requestSequence += 1;
    dismissPredictions();
    if (resetToken) freshToken();
  }

  function fieldEnabled() {
    return !destroyed && !input.matches(':disabled');
  }

  function captureOwner() {
    return { sequence: requestSequence, session: getActiveSessionRevision() };
  }

  function ownsWork(owner) {
    return fieldEnabled() && googlePlacesAutocompleteAllowed()
      && owner.sequence === requestSequence
      && owner.session === getActiveSessionRevision();
  }

  const unsubscribeSession = subscribeActiveSessionRevision(() => {
    const revision = getActiveSessionRevision();
    if (!destroyed && revision !== observedSessionRevision) {
      observedSessionRevision = revision;
      invalidate();
      if (loading) {
        loadSequence += 1;
        loading = false;
        publishLoadState('unavailable', 'Sign-in changed. Try again to enable address suggestions.', true);
      }
    }
  });

  function paintActive() {
    Array.from(list.children).forEach((node, index) => {
      const isActive = index === activeIndex;
      node.setAttribute('aria-selected', isActive ? 'true' : 'false');
      node.classList.toggle('bg-stone-100', isActive);
    });
  }

  function renderList() {
    list.replaceChildren();
    suggestions.forEach((suggestion, index) => {
      const { main, secondary } = predictionLabel(suggestion.placePrediction);
      const option = document.createElement('li');
      option.setAttribute('role', 'option');
      option.className = 'cursor-pointer px-3 py-2 text-sm text-stone-700 hover:bg-stone-100';
      const primary = document.createElement('span');
      primary.textContent = main;
      option.appendChild(primary);
      if (secondary) {
        const detail = document.createElement('span');
        detail.className = 'ml-2 text-xs text-stone-400';
        detail.textContent = secondary;
        option.appendChild(detail);
      }
      // mousedown, not click: the field must not lose focus and close the list
      // before the selection is handled.
      option.addEventListener('mousedown', (event) => {
        event.preventDefault();
        choose(index);
      });
      list.appendChild(option);
    });
    panel.hidden = suggestions.length === 0;
    input.setAttribute('aria-expanded', suggestions.length ? 'true' : 'false');
    paintActive();
  }

  async function choose(index) {
    const suggestion = suggestions[index];
    if (!suggestion || !fieldEnabled() || !googlePlacesAutocompleteAllowed()) return;
    const owner = captureOwner();
    dismissPredictions();
    try {
      const place = suggestion.placePrediction.toPlace();
      // fetchFields ends the prediction's Google session. Reserve a fresh token
      // for subsequent typing now, never in an obsolete completion's finally.
      freshToken();
      await place.fetchFields({ fields: REQUESTED_FIELDS[kind] });
      if (!ownsWork(owner)) return;
      const selection = NORMALIZERS[kind](place);
      if (!selection) return;

      if (kind === ADDRESS_SELECTION) {
        if (selection.street) {
          input.value = selection.street;
          onChange?.(selection.street);
        }
        if (!ownsWork(owner)) return;
        onSelect?.(selection);
      } else {
        input.value = '';
        onSelect?.(selection);
      }
    } catch {
      // Details could not be fetched. Whatever the user typed stays put and no
      // selection is reported, rather than clearing or half-filling the field.
    }
  }

  async function requestSuggestions(query, owner, prediction) {
    debounceId = null;
    if (!library || !ownsWork(owner) || prediction !== predictionSequence) return;
    try {
      const response = await library.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        sessionToken,
        includedRegionCodes: INCLUDED_REGION_CODES,
        includedPrimaryTypes: INCLUDED_PRIMARY_TYPES,
      });
      if (!ownsWork(owner) || prediction !== predictionSequence) return;
      predictionPending = false;
      suggestions = (response?.suggestions || [])
        .filter((item) => item?.placePrediction)
        .slice(0, MAXIMUM_SUGGESTIONS);
      activeIndex = -1;
      renderList();
    } catch {
      if (ownsWork(owner) && prediction === predictionSequence) {
        predictionPending = false;
        closeList();
      }
    }
  }

  const handleInput = () => {
    if (!fieldEnabled()) return;
    invalidate({ resetToken: false });
    const owner = captureOwner();
    const prediction = predictionSequence;
    const query = input.value.trim();
    onChange?.(input.value);
    if (!library || !ownsWork(owner) || query.length < MINIMUM_QUERY_LENGTH) return;
    predictionPending = true;
    debounceId = setTimeout(() => requestSuggestions(query, owner, prediction), SUGGEST_DEBOUNCE_MS);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && (!panel.hidden || predictionPending)) {
      // Preserve normal dialog Escape behavior when no choices are visible.
      if (!panel.hidden) event.stopPropagation();
      dismissPredictions();
      return;
    }
    if (panel.hidden || !suggestions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      activeIndex = (activeIndex + 1) % suggestions.length;
      paintActive();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      activeIndex = activeIndex <= 0 ? suggestions.length - 1 : activeIndex - 1;
      paintActive();
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      choose(activeIndex);
    }
  };

  const handleBlur = () => {
    if (!destroyed) dismissPredictions();
  };

  input.addEventListener('input', handleInput);
  input.addEventListener('keydown', handleKeyDown);
  input.addEventListener('blur', handleBlur);

  void loadFieldLibrary();

  return {
    element: input,
    setValue(next) {
      const text = String(next ?? '');
      if (!destroyed && input.value !== text) {
        invalidate();
        input.value = text;
      }
    },
    setDisabled(next) {
      if (destroyed) return;
      const nextDisabled = Boolean(next);
      if (nextDisabled && !input.disabled) invalidate();
      input.disabled = nextDisabled;
    },
    setPlaceholder(next) {
      if (!destroyed) input.placeholder = next ?? '';
    },
    focus() {
      if (!destroyed) input.focus();
    },
    retry() {
      return loadFieldLibrary(true);
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      loadSequence += 1;
      invalidate({ resetToken: false });
      unsubscribeSession();
      input.removeEventListener('input', handleInput);
      input.removeEventListener('keydown', handleKeyDown);
      input.removeEventListener('blur', handleBlur);
      retryButton.removeEventListener('click', handleRetry);
      loadStatus.remove();
      panel.remove();
      input.remove();
      library = null;
      sessionToken = null;
    },
  };
}
